/**
 * Cross-tool File handoff.
 *
 * Lets one disk-image tool open another in a new tab and pre-load the same
 * File reference, settings, and intent — without forcing the user to re-pick.
 * Browser security forbids serialising a File across tabs, so the trick is:
 *
 *   Source tab:
 *     - generates handoffId
 *     - stashes { file, payload } on window.__DiskImageHandoff
 *     - persists { fileMeta, payload, originUrl } in IndexedDB cluster_kv
 *     - window.open('/<target>#disk-handoff=<id>', '_blank')
 *
 *   Target tab:
 *     - reads url hash → handoffId
 *     - tries window.opener.__DiskImageHandoff[handoffId] (same-origin: works)
 *     - if missing/closed, falls back to IndexedDB → empty-state hint:
 *         "We were going to receive '<filename>' from /<source> — please
 *          drop it again."
 *
 * Surface: window.__DiskImage.Handoff
 *   send(targetSlug, { file, payload }) → opens new tab, returns handoffId
 *   consume() → Promise<{ file?, payload? } | null>
 *   peek()    → { handoffId, fileMeta?, payload? } | null   (no consumption)
 *   clear()   → wipes pending/consumed records
 */
(function (w) {
    'use strict';
    w.__DiskImage = w.__DiskImage || {};
    if (w.__DiskImage.Handoff) return;

    var KV_KEY = 'handoff_pending';
    var Persistence = w.__DiskImage.Persistence;

    if (!w.__DiskImageHandoff) w.__DiskImageHandoff = {};

    function _genId() {
        var s = '';
        for (var i = 0; i < 16; i++) s += (Math.floor(Math.random() * 36)).toString(36);
        return s;
    }

    function _meta(file) {
        if (!file) return null;
        return { name: file.name, size: file.size, type: file.type || '', lastModified: file.lastModified || 0 };
    }

    function send(targetSlug, opts) {
        opts = opts || {};
        var id = _genId();
        var record = {
            id: id,
            file: opts.file || null,
            payload: opts.payload || null,
            sourceSlug: opts.sourceSlug || null,
            createdAt: Date.now()
        };
        w.__DiskImageHandoff[id] = record;
        if (Persistence) {
            Persistence.putNow('cluster_kv', KV_KEY, {
                id: id,
                fileMeta: _meta(opts.file),
                payload: opts.payload || null,
                sourceSlug: opts.sourceSlug || null
            }).catch(function () {});
        }
        var locale = (w.__LOCALE || 'en');
        var path = '/' + locale + '/' + targetSlug + '#disk-handoff=' + id;
        var win = w.open(path, '_blank', 'noopener=no,noreferrer=no');
        if (!win) {
            // Popup blocked — fall back to same-tab navigation, the receiver will rely on IDB
            w.location.href = path;
        }
        return id;
    }

    function _readIdFromHash() {
        var h = w.location.hash || '';
        var m = h.match(/(?:^#|&)disk-handoff=([0-9a-z]+)/i);
        return m ? m[1] : null;
    }

    function consume() {
        var id = _readIdFromHash();
        if (!id) return Promise.resolve(null);

        // Strip the handoff token from the URL so a refresh doesn't replay
        try {
            var newHash = (w.location.hash || '').replace(/(?:^#|&)disk-handoff=[0-9a-z]+/i, '');
            if (newHash === '#') newHash = '';
            w.history.replaceState(null, '', w.location.pathname + w.location.search + newHash);
        } catch (_) {}

        // Try opener first
        try {
            var op = w.opener;
            if (op && op.__DiskImageHandoff && op.__DiskImageHandoff[id]) {
                var rec = op.__DiskImageHandoff[id];
                delete op.__DiskImageHandoff[id];
                return Promise.resolve({
                    file: rec.file || null,
                    payload: rec.payload || null,
                    sourceSlug: rec.sourceSlug || null,
                    fromOpener: true
                });
            }
        } catch (_) {
            // Cross-origin or opener gone
        }

        // Fall back to IDB metadata
        if (!Persistence) return Promise.resolve(null);
        return Persistence.get('cluster_kv', KV_KEY).then(function (rec) {
            if (!rec || rec.id !== id) return null;
            // Don't expose `file` (we don't have it); caller shows "drop again"
            Persistence.delete('cluster_kv', KV_KEY).catch(function () {});
            return {
                file: null,
                payload: rec.payload || null,
                sourceSlug: rec.sourceSlug || null,
                fileMeta: rec.fileMeta || null,
                fromOpener: false
            };
        });
    }

    function peek() {
        var id = _readIdFromHash();
        if (!id) return null;
        try {
            var op = w.opener;
            if (op && op.__DiskImageHandoff && op.__DiskImageHandoff[id]) {
                var r = op.__DiskImageHandoff[id];
                return { handoffId: id, fileMeta: _meta(r.file), payload: r.payload };
            }
        } catch (_) {}
        return { handoffId: id, fileMeta: null, payload: null };
    }

    function clear() {
        try {
            Object.keys(w.__DiskImageHandoff).forEach(function (k) { delete w.__DiskImageHandoff[k]; });
        } catch (_) {}
        if (Persistence) Persistence.delete('cluster_kv', KV_KEY).catch(function () {});
    }

    w.__DiskImage.Handoff = {
        send: send,
        consume: consume,
        peek: peek,
        clear: clear
    };
})(window);
